import { Mark, mergeAttributes } from '@tiptap/core'

export interface CommentMarkOptions {
  HTMLAttributes: Record<string, any>
  onCommentClick?: (commentId: string) => void
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    commentHighlight: {
      setCommentHighlight: (attributes: { commentId: string }) => ReturnType
      unsetCommentHighlight: () => ReturnType
      resolveCommentHighlight: (commentId: string) => ReturnType
      removeCommentHighlight: (commentId: string) => ReturnType
    }
  }
}

export const CommentMarkExtension = Mark.create<CommentMarkOptions>({
  name: 'commentHighlight',

  inclusive: false,

  addOptions() {
    return {
      HTMLAttributes: {},
      onCommentClick: undefined,
    }
  },

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-comment-id'),
        renderHTML: (attributes) => {
          if (!attributes.commentId) return {}
          return { 'data-comment-id': attributes.commentId }
        },
      },
      resolved: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-resolved') === 'true',
        renderHTML: (attributes) => {
          if (!attributes.resolved) return {}
          return { 'data-resolved': 'true' }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-comment-id]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: HTMLAttributes.resolved
          ? 'comment-highlight comment-highlight--resolved'
          : 'comment-highlight',
      }),
      0,
    ]
  },

  addCommands() {
    return {
      setCommentHighlight:
        (attributes) =>
        ({ commands }) => {
          return commands.setMark(this.name, attributes)
        },
      unsetCommentHighlight:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name)
        },
      resolveCommentHighlight:
        (commentId: string) =>
        ({ tr, state }) => {
          const { doc } = state
          doc.descendants((node, pos) => {
            node.marks.forEach((mark) => {
              if (
                mark.type.name === this.name &&
                mark.attrs.commentId === commentId
              ) {
                tr.addMark(
                  pos,
                  pos + node.nodeSize,
                  mark.type.create({ ...mark.attrs, resolved: true }),
                )
              }
            })
          })
          return true
        },
      removeCommentHighlight:
        (commentId: string) =>
        ({ tr, state }) => {
          const { doc } = state
          const markType = state.schema.marks[this.name]
          if (!markType) return false
          doc.descendants((node, pos) => {
            node.marks.forEach((mark) => {
              if (
                mark.type.name === this.name &&
                mark.attrs.commentId === commentId
              ) {
                tr.removeMark(pos, pos + node.nodeSize, markType)
              }
            })
          })
          return true
        },
    }
  },
})
